import { useState } from 'react'
import { useCopy } from '@/lib/use-copy'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { cn } from '@/lib/cn'
import { QrCode } from '@/components/QrCode'
import { HOLDINGS } from './crypto-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

interface Deposit {
  readonly network: string
  readonly address: string
  /**
   * What the QR encodes. Wallets expect a payment URI, not a bare address:
   * BIP-21 for Bitcoin, EIP-681 for EVM chains (8453 is Base, and USDC is a
   * token transfer to its contract), and the Solana Pay scheme for Solana.
   */
  readonly uri: string
}

const BASE_USDC = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
const WALLET_EVM = '0x7f3a9c21b4e8d05f6a1c9e2b7d4f8a03c5e91f2a'
const WALLET_SOL = '9xQeWvG816bUx9EPa2Ld1CUu4sHVmYqRDMhbTpvMTx4z'
const WALLET_BTC = 'bc1q9d4ywgfnd8h43da5tpcxcn6ajv590cg6d3tg7l'

const ADDRESSES: Record<string, readonly Deposit[]> = {
  USDC: [
    {
      network: 'Base',
      address: WALLET_EVM,
      uri: `ethereum:${BASE_USDC}@8453/transfer?address=${WALLET_EVM}`,
    },
    { network: 'Solana', address: WALLET_SOL, uri: `solana:${WALLET_SOL}` },
  ],
  BTC: [{ network: 'Bitcoin', address: WALLET_BTC, uri: `bitcoin:${WALLET_BTC}` }],
  ETH: [{ network: 'Base', address: WALLET_EVM, uri: `ethereum:${WALLET_EVM}@8453` }],
}

export function CryptoReceiveScreen() {
  const [copied, copy] = useCopy()
  const [symbol, setSymbol] = useState('USDC')
  const [networkIndex, setNetworkIndex] = useState(0)
  const options = ADDRESSES[symbol] ?? []
  const chosen = options[networkIndex] ?? options[0]

  return (
    <>
      <PageHeader
        title="Receive crypto"
        subtitle="Free to receive. Make sure the sender uses the same network — funds sent on another one are lost."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/crypto">
            Back to wallet
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Which asset?
            </Text>
            <div className={`${styles.corridors} pb-mt-4`}>
              {HOLDINGS.map((item) => (
                <button
                  key={item.symbol}
                  type="button"
                  className={cn(styles.corridor, item.symbol === symbol && styles.corridorActive)}
                  onClick={() => {
                    setSymbol(item.symbol)
                    setNetworkIndex(0)
                  }}
                  aria-pressed={item.symbol === symbol}
                >
                  <Stack gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {item.symbol}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {item.name}
                    </Text>
                  </Stack>
                </button>
              ))}
            </div>

            {options.length > 1 && (
              <>
                <Text variant="caption" weight="semibold" className="pb-mt-5">
                  On which network?
                </Text>
                <Stack direction="row" gap={2} wrap className="pb-mt-3">
                  {options.map((option, index) => (
                    <Button
                      key={option.network}
                      variant={index === networkIndex ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => setNetworkIndex(index)}
                    >
                      {option.network}
                    </Button>
                  ))}
                </Stack>
              </>
            )}

            <Card.Footer>
              <Stack direction="row" align="start" gap={3}>
                <Icon name="warning" size={20} />
                <Text variant="caption" tone="secondary">
                  Send only {symbol} on {chosen?.network}. Anything else sent to this address cannot
                  be recovered, by us or by anyone.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                Your {symbol} address
              </Text>
              <Pill tone="accent">{chosen?.network}</Pill>
            </Card.Header>

            <Stack align="center" gap={4} className="pb-mt-4">
              <QrCode value={chosen?.uri ?? ''} label={`${symbol} deposit address`} />
              <Text
                variant="caption"
                tone="onDark"
                style={{ wordBreak: 'break-all', textAlign: 'center', fontFamily: 'var(--pb-font-mono)' }}
              >
                {chosen?.address}
              </Text>
            </Stack>

            <div className={checkout.rule} />

            {[
              ['Fee to receive', 'Free'],
              ['Arrives', 'After network confirmation'],
              ['Minimum', 'None'],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text variant="caption" weight="semibold" tone="onDark">
                  {value}
                </Text>
              </div>
            ))}

            <Card.Footer>
              <Stack direction="row" gap={2} wrap>
                <Button
                  size="md"
                  iconStart={copied ? 'check' : 'content_copy'}
                  onClick={() => copy(chosen?.address ?? '')}
                >
                  {copied ? 'Copied' : 'Copy address'}
                </Button>
                <Button variant="onDark" size="md" iconStart="ios_share">
                  Share
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
